// components/monitoring/T247WanModal.js
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { fmt2, DualAreaSpark, safeArr } from "./t247Shared";
import { apiFetchJSON } from "../../api/apiClient";
import { reportPriorityLabel } from "../../utils/wanPriority";

const TELEMETRY_POLL_MS = 60000;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");
const letter = (n) => String.fromCharCode(65 + n); // 0 → A, 1 → B, 2 → C ...

// Static legend + peak reading for the throughput spark. No hover state, no
// event handlers - just gives the two lines a color key and a number so the
// chart isn't "just a shape". Colors match DualAreaSpark's own down/up colors.
// const SparkLegend = ({ peakDown, peakUp, unit = "GB/hr" }) => (
//   <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "0 20px 8px", font: "400 10px 'DM Mono',monospace", letterSpacing: "0.04em" }}>
//     <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-dim)" }}>
//       <span style={{ width: 10, height: 10, background: "#D7D7CC", display: "inline-block" }} />
//       DOWNLOAD · PEAK {fmt2(peakDown)} {unit}
//     </span>
//     <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-dim)" }}>
//       <span style={{ width: 10, height: 10, background: "#88F4EB", display: "inline-block" }} />
//       UPLOAD · PEAK {fmt2(peakUp)} {unit}
//     </span>
//   </div>
// );

// Pull this interface's own hourly down/up series (if the backend provided per-WAN detail).
// const getInterfaceSeries = (deviceData, wanId) => {
//   const details = deviceData?.hourlyWanUsagesLogs?.details;
//   if (!details || wanId === undefined) return { down: [], up: [] };
//   const series = details[String(wanId)] || [];
//   return {
//     down: series.map((e) => (e.down || 0) / 1024),
//     up: series.map((e) => (e.up || 0) / 1024),
//   };
// };

const getStarlinkForInterface = (starlinkData, iface) => {
  if (!starlinkData || !iface) return null;
  const data = starlinkData.starlink_status || starlinkData.data;
  if (!data) return null;
  const list = Array.isArray(data) ? data : [data];
  return list.find((s) => Math.floor(parseFloat(s.wan_interface_id)) === parseFloat(iface.id)) || null;
};

const T247WanModal = ({ row, deviceData, onClose, userType }) => {
  const navigate = useNavigate();
  const iface = row?.raw || {};
  const cellularMatch = safeArr(deviceData?.cellularInterfaces).find((c) => c.id === iface.id || c.name === iface.name);
  const starlink = row?.isCellular ? null : getStarlinkForInterface(deviceData?.starlinkData, iface);
  // Row built from usage data only (no live WAN status in the report) -
  // see computeWanRows fallback in t247Shared.
  const isSynthesized = row?.synthesized === true;
  const hasPriority = row?.priority !== undefined && row?.priority !== null && row?.priority !== "—";
  const typeLabel = starlink || iface.virtual_type === "starlink"
    ? "Starlink"
    : row?.isCellular ? "Cellular" : capitalize(iface.type) || "—";
  // const series = getInterfaceSeries(deviceData, iface.id);

  // Telemetry state - starts from whatever the collector already cached
  // (starlink.downlinkThroughputBps etc, written to the daily archive on
  // the last poll), then refreshes from a live ELCOME call every 60s
  // via the same StarlinkProvider.full_detail() that powers
  // StarlinkDevicePage. ut_id is ELCOME's own terminal ID, already
  // present on the cached record - no new backend endpoint needed.
  // The cached ut_id comes prefixed (e.g. "ut01000000-00000000-00240888"),
  // but the terminal-detail endpoint expects the bare ID without it.
  const utId = starlink?.ut_id?.replace(/^ut/, '');
  const [liveTelemetry, setLiveTelemetry] = useState(() => (starlink ? {
    downlinkMbps: starlink.downlinkThroughputBps != null ? starlink.downlinkThroughputBps / 1e6 : null,
    uplinkMbps: starlink.uplinkThroughputBps != null ? starlink.uplinkThroughputBps / 1e6 : null,
    latencyMs: starlink.popPingLatencyMs ?? null,
    signalPercent: null,       // not in the cached archive - only from a live call
    pingDropPercent: null,     // not in the cached archive - only from a live call
    obstructionPercent: null,  // not in the cached archive - only from a live call
    source: 'cached',
  } : null));

  useEffect(() => {
    if (!utId || !userType) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const json = await apiFetchJSON(`/api/v2/${userType}/starlink/${utId}`);
        if (!cancelled && json?.live) {
          setLiveTelemetry({ ...json.live, serviceLine: json.serviceLine, source: 'live' });
        }
      } catch {
        // Silent - the cached values stay on screen, no error state for
        // a background refresh failure.
      }
    };

    poll();
    const interval = setInterval(poll, TELEMETRY_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [utId, userType]);

  if (!row) return null;


  // Letter physical SIMs and eSIMs independently: SIM A, SIM B, ESIM A, ESIM B, ESIM C ...
  let simCount = 0;
  let esimCount = 0;
  const simLabels = safeArr(cellularMatch?.sims).map((sim) =>
    sim?.is_esim ? `ESIM ${letter(esimCount++)}` : `SIM ${letter(simCount++)}`
  );

  const content = (
    <div className="t247-modal-overlay t247" onClick={onClose}>
      <div className="t247-modal" onClick={(e) => e.stopPropagation()}>
        <div className="t247-modal-header">
          <span className="t247-modal-title">{starlink ? "STARLINK INTERFACE" : row.isCellular ? "CELLULAR INTERFACE" : "WAN INTERFACE"}</span>
          <button className="t247-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="t247-modal-info">
          <span>{row.name}</span>
          <span>{row.ip}</span>
          <span className={`t247-chip ${isSynthesized ? "chip-other" : row.isDown ? "chip-down" : row.isCellular ? "chip-cell" : "chip-up"}`}>{row.statusLabel}</span>
        </div>

        <div className="t247-modal-body">
          {isSynthesized && (
            <div className="t247-modal-row" style={{ font: "400 11px 'DM Mono',monospace", color: "var(--text-dim)", letterSpacing: "0.02em" }}>
              Live interface status wasn't available for this report (the device may have been offline when it was built). Usage below comes from the usage logs; IP, priority and SIM details aren't shown.
            </div>
          )}
          <div className="t247-modal-section-title">USAGE</div>
          <div className="t247-modal-grid">
            <Cell k="↓ DOWNLOAD" v={`${fmt2(row.download)} GB`} />
            <Cell k="↑ UPLOAD" v={`${fmt2(row.upload)} GB`} />
            <Cell k="Σ TOTAL" v={`${fmt2(row.total)} GB`} />
            <Cell k="SHARE OF TRAFFIC" v={`${row.pct.toFixed(1)}%`} />
          </div>

          <div className="t247-modal-section-title">CONNECTION</div>
          <div className="t247-modal-grid">
            <Cell k="PRIORITY" v={reportPriorityLabel(row.priority)} />
            <Cell k="TYPE" v={typeLabel} />
            {iface.netmask && <Cell k="SUBNET MASK" v={iface.netmask} />}
            {iface.gateway && <Cell k="GATEWAY" v={iface.gateway} />}
          </div>

          {row.isCellular && cellularMatch && (
            <>
              <div className="t247-modal-section-title">CELLULAR</div>
              <div className="t247-modal-grid">
                {cellularMatch.carrier_name && <Cell k="CARRIER" v={cellularMatch.carrier_name} />}
                {cellularMatch.network && <Cell k="NETWORK" v={cellularMatch.network} />}
              </div>
              {safeArr(cellularMatch.sims).map((sim, i) => (
                <div className="t247-modal-row" key={`${sim?.iccid || "sim"}-${i}`}>
                  <span style={{ font: "700 12px 'DM Sans',sans-serif", flex: 1 }}>
                    {simLabels[i]}{" "}
                    <span style={{ fontWeight: 400, fontFamily: "'DM Mono',monospace", fontSize: 10.5, color: "var(--text-muted)" }}>
                      {sim?.iccid || "—"}
                    </span>
                  </span>
                  <span className={`t247-chip ${sim?.active ? "chip-up" : "chip-down"}`}>
                    {sim?.active ? "ACTIVE" : "INACTIVE"}
                  </span>
                </div>
              ))}
            </>
          )}


          {starlink && (
            <>
              <div className="t247-modal-section-title">
                STARLINK TELEMETRY
                <span className="t247-modal-telemetry-source">
                  {liveTelemetry?.source === 'live' ? 'LIVE' : 'FROM LAST SYNC'}
                </span>
              </div>
              <div className="t247-modal-grid">
                <Cell k="↓ DOWNLINK THROUGHPUT" v={liveTelemetry?.downlinkMbps != null ? `${liveTelemetry.downlinkMbps.toFixed(2)} Mbps` : "N/A"} />
                <Cell k="↑ UPLINK THROUGHPUT" v={liveTelemetry?.uplinkMbps != null ? `${liveTelemetry.uplinkMbps.toFixed(2)} Mbps` : "N/A"} />
                <Cell k="LATENCY" v={liveTelemetry?.latencyMs != null ? `${Math.round(liveTelemetry.latencyMs)} ms` : "N/A"} />
                <Cell k="PING DROP" v={liveTelemetry?.pingDropPercent != null ? `${liveTelemetry.pingDropPercent}%` : "N/A"} />
                <Cell k="SIGNAL QUALITY" v={liveTelemetry?.signalPercent != null ? `${liveTelemetry.signalPercent}%` : "N/A"} />
                <Cell k="OBSTRUCTION" v={liveTelemetry?.obstructionPercent != null ? `${liveTelemetry.obstructionPercent}%` : "N/A"} />
              </div>

              {/* Identity/config fields - these don't change on a live poll
                  (service class, mobility class, boresight angles etc are
                  effectively static), so they're kept separate from the
                  telemetry above and always shown, rather than being
                  swapped out once live data arrives. */}
              <div className="t247-modal-section-title">STARLINK INFO</div>
              <div className="t247-modal-grid">
                <Cell k="TERMINAL ID" v={starlink.ut_id ? starlink.ut_id.replace(/^ut/, '') : "N/A"} />
                <Cell k="SERVICE CLASS" v={starlink.classOfService || "N/A"} />
                <Cell k="MOBILITY CLASS" v={starlink.mobilityClass || "N/A"} />
                <Cell k="SOFTWARE UPDATE" v={starlink.softwareUpdateState || "UNKNOWN"} />
                <Cell k="DISABLEMENT CODE" v={starlink.disablementCode || "UNKNOWN"} />
                <Cell k="SNR STATUS" v={starlink.isSnrAboveNoiseFloor === true ? "Good" : starlink.isSnrAboveNoiseFloor === false ? "Poor" : "N/A"} />
                <Cell k="ETH SPEED" v={starlink.ethSpeedMbps != null ? `${starlink.ethSpeedMbps.toFixed(0)} Mbps` : "N/A"} />
                <Cell k="UPTIME" v={starlink.device_uptime || "N/A"} />
                <Cell k="AZIMUTH" v={starlink.boresightAzimuthDeg != null && starlink.boresightAzimuthDeg !== "N/A" ? `${starlink.boresightAzimuthDeg.toFixed(0)}°` : "N/A"} />
                <Cell k="ELEVATION" v={starlink.boresightElevationDeg != null && starlink.boresightElevationDeg !== "N/A" ? `${starlink.boresightElevationDeg.toFixed(0)}°` : "N/A"} />
              </div>
            </>
          )}
        </div>

        <div className="t247-modal-footer">
          <button className="t247-btn t247-btn-primary" onClick={onClose}>CLOSE</button>
          {starlink && utId && liveTelemetry?.serviceLine ? (
            <button
              className="t247-btn t247-btn-ghost"
              onClick={() => {
                onClose?.();
                navigate(`/admin/starlink/${encodeURIComponent(utId)}`);
              }}
            >
              ACCESS STARLINK PAGE
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.getElementById("modal-root") || document.body);
};

const Cell = ({ k, v }) => (
  <div className="t247-modal-cell">
    <div className="k">{k}</div>
    <div className="v">{v}</div>
  </div>
);

export default T247WanModal;
